import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  ListItemDecorator,
  Option,
  Select,
  Typography,
} from "@mui/joy";
import { ApiVenueDetail } from "griptonite-api-client";
import sortBy from "lodash/sortBy";
import { useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";

import { useAvailableVenuesQuery } from "../../hooks/venues";

export const VenueItem = ({
  venue,
  showName = true,
}: {
  venue?: ApiVenueDetail;
  showName?: boolean;
}) => {
  return (
    <ListItemDecorator>
      <Box sx={{ marginRight: showName ? 1 : 0 }}>
        {venue?.logo?.url ? (
          <img
            src={venue.logo.url}
            alt={venue.name}
            style={{
              width: 24,
              height: 24,
              objectFit: "cover",
              borderRadius: "4px",
            }}
          />
        ) : (
          <Box
            sx={{
              width: 24,
              height: 24,
              backgroundColor: "neutral.200",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "10px",
              fontWeight: "bold",
              color: "neutral.600",
            }}
          >
            {venue?.name?.charAt(0).toUpperCase() || "?"}
          </Box>
        )}
      </Box>
      <Typography fontWeight="bold" fontSize={14} sx={{ display: showName ? "block" : "none" }}>
        {venue?.name}
      </Typography>
    </ListItemDecorator>
  );
};

export interface VenueSelectProps {
  name: string;
  label?: string;
  helperText?: string;
  multiple?: boolean;
  placeholder?: string;
}

export default function VenueSelect({
  name,
  label,
  helperText,
  multiple = false,
  placeholder,
}: VenueSelectProps) {
  const { data: venues = [] } = useAvailableVenuesQuery();
  const form = useFormContext();

  const {
    field: { value, onChange, onBlur },
    fieldState: { error },
  } = useController({
    name,
    control: form.control,
  });

  const sortedVenues = useMemo(() => sortBy(venues, ["name"]), [venues]);

  const handleChange = (
    _event: React.SyntheticEvent | null,
    newValue: string | string[] | null,
  ) => {
    onChange(newValue);
  };

  const getSelectedVenueDisplay = (selectedValue: string[] | string) => {
    if (multiple && Array.isArray(selectedValue)) {
      if (selectedValue.length === 0) {
        return placeholder || "Select venues";
      }
      const selectedVenues = sortedVenues.filter((v) => selectedValue.includes(v.uuid));
      return (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            alignItems: "center",
          }}
        >
          {selectedVenues.map((venue) => (
            <VenueItem key={venue.uuid} venue={venue} showName={true} />
          ))}
        </Box>
      );
    } else {
      const venue = sortedVenues.find((v) => v.uuid === selectedValue);
      return venue ? (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <VenueItem venue={venue} />
        </Box>
      ) : (
        placeholder || "Select venue"
      );
    }
  };

  return (
    <FormControl error={!!error}>
      {label && <FormLabel>{label}</FormLabel>}
      <Select
        multiple={multiple}
        value={value || (multiple ? [] : "")}
        onChange={handleChange}
        onBlur={onBlur}
        placeholder={placeholder}
        renderValue={(selected) => {
          if (!selected) return placeholder;
          return getSelectedVenueDisplay(value);
        }}
      >
        {sortedVenues.map((venue) => (
          <Option key={venue.uuid} value={venue.uuid}>
            <VenueItem venue={venue} />
          </Option>
        ))}
      </Select>
      {(error || helperText) && <FormHelperText>{error?.message || helperText}</FormHelperText>}
    </FormControl>
  );
}
